import apiClient from './apiClient';

export type CalendarScope = 'global' | 'project';
export type CalendarDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type ExceptionType = 'holiday' | 'blackout' | 'working';

export interface CalendarListItem {
  id: string;
  name: string;
  projectId: string | null;
  projectName: string | null;
  scope: CalendarScope;
  isActive: boolean;
  workingDaysMask: number;
  workingDays: CalendarDay[];
  exceptionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarException {
  id: string;
  exceptionDate: string;
  exceptionType: ExceptionType;
  description: string | null;
  createdAt: string;
}

export interface CalendarDetail extends CalendarListItem {
  exceptions: CalendarException[];
}

// Bit convention matches taskReadiness.service.isWorkingDayByMask on the
// backend (Sun=1, Mon=64 … Sat=2, total Mon-Fri = 124).
export const DAY_BIT: Record<CalendarDay, number> = {
  mon: 64,
  tue: 32,
  wed: 16,
  thu: 8,
  fri: 4,
  sat: 2,
  sun: 1,
};

export const DAY_LABEL: Record<CalendarDay, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export const daysToMask = (days: CalendarDay[]): number =>
  days.reduce((sum, d) => sum + (DAY_BIT[d] || 0), 0);

export const maskToDays = (mask: number): CalendarDay[] =>
  (Object.entries(DAY_BIT) as Array<[CalendarDay, number]>)
    .filter(([, bit]) => (mask & bit) === bit)
    .map(([day]) => day);

class CalendarService {
  async list(): Promise<CalendarListItem[]> {
    const response: any = await apiClient.get('/calendars');
    return response.data ?? [];
  }

  async get(id: string, year?: number): Promise<CalendarDetail | null> {
    const params: Record<string, string> = {};
    if (year) params.year = String(year);
    const response: any = await apiClient.get(`/calendars/${id}`, { params });
    return response.data ?? null;
  }

  async create(payload: {
    projectId?: string | null;
    name: string;
    workingDaysMask?: number;
    isActive?: boolean;
  }): Promise<CalendarListItem> {
    const response: any = await apiClient.post('/calendars', payload);
    return response.data;
  }

  async update(
    id: string,
    patch: { name?: string; workingDaysMask?: number; isActive?: boolean }
  ): Promise<CalendarListItem> {
    const response: any = await apiClient.patch(`/calendars/${id}`, patch);
    return response.data;
  }

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/calendars/${id}`);
  }

  async addException(
    calendarId: string,
    payload: { exceptionDate: string; exceptionType: ExceptionType; description?: string }
  ): Promise<CalendarException> {
    const response: any = await apiClient.post(`/calendars/${calendarId}/exceptions`, payload);
    return response.data;
  }

  async removeException(exceptionId: string): Promise<void> {
    await apiClient.delete(`/calendars/exceptions/${exceptionId}`);
  }
}

export default new CalendarService();
